import React, { useState } from "react";
import { CloseIcon } from "./icons/CloseIcon";

interface Tech {
  id: string;
  title: string;
}

interface ResearcherCardProps {
  /** A % (number), a status line (string), or null / '' for no badge. */
  relevanceScore: number | string | null;
  title: string;
  institution: string;
  location: string;
  techs: Tech[];
  onTechCardClick: (techId: string) => void;
  onClose: () => void;
  // API callback props
  onBookmarkToggle?: (isBookmarked: boolean) => void;
  onLikeDislike?: (liked: boolean | null) => void;
  initialBookmarked?: boolean;
  initialLiked?: boolean | null;
}

export default function ResearcherCard({
  relevanceScore,
  title,
  institution,
  location,
  techs,
  onTechCardClick,
  onClose,
  onBookmarkToggle,
  onLikeDislike,
  initialBookmarked = false,
  initialLiked = null,
}: ResearcherCardProps) {
  const [showAllTechs, setShowAllTechs] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(initialBookmarked);
  const [feedback, setFeedback] = useState<"like" | "dislike" | null>(
    initialLiked === true ? "like" : initialLiked === false ? "dislike" : null
  );

  const displayedTechs = showAllTechs ? techs : techs.slice(0, 5);
  // No badge without a score rather than an empty pill.
  const hasBadge = typeof relevanceScore === 'number'
    ? isFinite(relevanceScore)
    : relevanceScore !== null && relevanceScore !== undefined && String(relevanceScore).trim() !== '';

  const handleBookmarkClick = () => {
    console.log('🔖 ResearcherCard bookmark clicked:', {
      isBookmarked: isBookmarked,
      onBookmarkToggle: !!onBookmarkToggle,
      onBookmarkToggleType: typeof onBookmarkToggle
    });
    
    const newBookmarked = !isBookmarked;
    setIsBookmarked(newBookmarked);
    
    if (onBookmarkToggle) {
      console.log('🔖 Calling onBookmarkToggle with:', newBookmarked);
      onBookmarkToggle(newBookmarked);
    } else {
      console.warn('🔖 onBookmarkToggle is not provided to ResearcherCard');
    }
  };

  const handleLikeClick = () => {
    console.log('👍 ResearcherCard like clicked:', {
      currentFeedback: feedback,
      onLikeDislike: !!onLikeDislike,
      onLikeDislikeType: typeof onLikeDislike
    });
    
    const newFeedback = feedback === "like" ? null : "like";
    setFeedback(newFeedback);
    
    if (onLikeDislike) {
      const likedValue = newFeedback === "like" ? true : null;
      console.log('👍 Calling onLikeDislike with:', likedValue);
      onLikeDislike(likedValue);
    } else {
      console.warn('👍 onLikeDislike is not provided to ResearcherCard');
    }
  };

  const handleDislikeClick = () => {
    console.log('👎 ResearcherCard dislike clicked:', {
      currentFeedback: feedback,
      onLikeDislike: !!onLikeDislike,
      onLikeDislikeType: typeof onLikeDislike
    });
    
    const newFeedback = feedback === "dislike" ? null : "dislike";
    setFeedback(newFeedback);
    
    if (onLikeDislike) {
      const likedValue = newFeedback === "dislike" ? false : null;
      console.log('👎 Calling onLikeDislike with:', likedValue);
      onLikeDislike(likedValue);
    } else {
      console.warn('👎 onLikeDislike is not provided to ResearcherCard');
    }
  };

  return (
    <>
      {/* Desktop Version */}
      <div className="hidden sm:block utech-nocopy">
        <div className="w-full max-w-[434px] mx-auto">
          <div className="flex flex-col">
            <div className="relative w-[434px] h-[516px] flex flex-col items-center gap-[45px] p-3 rounded-xl">
              {/* Background SVG - positioned exactly as in Figma */}
              <svg
                width="434"
                height="508"
                viewBox="0 0 434 508"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="absolute inset-0 w-full h-[508px] pointer-events-none"
              >
                <path
                  d="M16.0029 0.5H120.016C129.661 0.500059 138.765 4.96144 144.675 12.585C150.774 20.4528 160.169 25.0576 170.124 25.0576H418C426.56 25.0576 433.5 31.9972 433.5 40.5576V492C433.5 500.56 426.56 507.5 418 507.5H299.713C289.691 507.5 280.278 502.69 274.405 494.568C265.519 482.278 251.274 475 236.107 475H16.0918C7.53274 475 0.593513 468.062 0.591797 459.503L0.50293 16.0029C0.501307 7.44147 7.44149 0.500151 16.0029 0.5Z"
                  fill="white"
                />
                <path
                  d="M16.0029 0.5H120.016C129.661 0.500059 138.765 4.96144 144.675 12.585C150.774 20.4528 160.169 25.0576 170.124 25.0576H418C426.56 25.0576 433.5 31.9972 433.5 40.5576V492C433.5 500.56 426.56 507.5 418 507.5H299.713C289.691 507.5 280.278 502.69 274.405 494.568C265.519 482.278 251.274 475 236.107 475H16.0918C7.53274 475 0.593513 468.062 0.591797 459.503L0.50293 16.0029C0.501307 7.44147 7.44149 0.500151 16.0029 0.5Z"
                  fill="#F0EAE1"
                  fillOpacity="0.3"
                />
                <path
                  d="M16.0029 0.5H120.016C129.661 0.500059 138.765 4.96144 144.675 12.585C150.774 20.4528 160.169 25.0576 170.124 25.0576H418C426.56 25.0576 433.5 31.9972 433.5 40.5576V492C433.5 500.56 426.56 507.5 418 507.5H299.713C289.691 507.5 280.278 502.69 274.405 494.568C265.519 482.278 251.274 475 236.107 475H16.0918C7.53274 475 0.593513 468.062 0.591797 459.503L0.50293 16.0029C0.501307 7.44147 7.44149 0.500151 16.0029 0.5Z"
                  stroke="#F0EAE1"
                  strokeWidth="1"
                />
              </svg>

              {/* Content positioned relative to the background */}
              <div className="relative z-10 w-full flex flex-col gap-3 pr-3 h-full pb-16">
                {/* Relevance Score Badge */}
                {hasBadge && (
                <div className="flex items-center gap-[2.79px] self-stretch">
                  <div className="flex items-center justify-center gap-[10px] px-2 py-1 bg-primary-100 rounded-[56px]">
                    <span className="text-[11px] font-normal text-primary-og-600 font-grotesk">
                      {typeof relevanceScore === 'number' ? `${relevanceScore}% relevance score` : relevanceScore}
                    </span>
                  </div>
                </div>
                )}

                {/* Title and Location */}
                <div className="flex flex-col items-start gap-2 self-stretch">
                  <h2 className="text-base font-medium text-grayscale-1300 leading-normal self-stretch font-grotesk">
                    {title}
                  </h2>
                  <div className="flex items-center gap-[11.159px] self-stretch">
                    <div className="flex items-center gap-[0.93px]">
                      <div className="flex w-4 h-4 flex-col justify-center items-center aspect-square">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-4 h-4 flex-shrink-0"
                        >
                          <path
                            d="M2.99991 6.49994C2.99991 10.9999 7.99991 14.4999 7.99991 14.4999C7.99991 14.4999 12.9999 10.9999 12.9999 6.49994C12.9999 5.17386 12.4731 3.90209 11.5354 2.96441C10.5978 2.02672 9.32599 1.49994 7.99991 1.49994C6.67383 1.49994 5.40206 2.02672 4.46437 2.9644C3.52669 3.90209 2.99991 5.17386 2.99991 6.49994Z"
                            fill="#9A7A4C"
                          />
                          <path
                            d="M8.00003 8.50003C6.89546 8.50003 6.00003 7.6046 6.00003 6.50003C6.00003 5.39546 6.89546 4.50003 8.00003 4.50003C9.1046 4.50003 10 5.39546 10 6.50003C10 7.6046 9.1046 8.50003 8.00003 8.50003Z"
                            stroke="white"
                            strokeWidth="0.929883"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <span className="text-[11px] font-normal text-grayscale-1000 font-grotesk">
                        {institution}, {location}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="w-full h-[0.5px] bg-primary-300"></div>

                {/* Tech Titles Section with scroll handling */}
                <div className="flex flex-col items-start gap-3 self-stretch flex-1 overflow-y-auto">
                  {displayedTechs.map((tech) => (
                    <button
                      key={tech.id}
                      onClick={() => onTechCardClick(tech.id)}
                      className="w-full text-left p-2 px-4 items-start self-stretch rounded-lg bg-secondary-300/20 hover:bg-secondary-300/30 transition-colors flex-shrink-0"
                    >
                      <span className="text-secondary-300 font-grotesk text-[11px] font-medium leading-normal underline">
                        {tech.title}
                      </span>
                    </button>
                  ))}
                  {techs.length > 5 && !showAllTechs && (
                    <div className="w-full flex justify-center flex-shrink-0">
                      <button
                        onClick={() => setShowAllTechs(true)}
                        className="text-primary-og-600 font-grotesk text-sm font-bold mt-2"
                      >
                        Show more
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="absolute bottom-4 left-4 right-4 flex justify-end items-center gap-3 pointer-events-auto z-20">
                <div className="flex justify-end items-end gap-3">
                  <button
                    onClick={handleLikeClick}
                    className="flex p-2 justify-center items-center gap-2 bg-primary-100 rounded-[32px]"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4"
                    >
                      <path
                        d="M13.5929 12.9705C13.4428 13.9461 12.6033 14.6663 11.6162 14.6663H2.66536C1.92898 14.6663 1.33203 14.0694 1.33203 13.333V8.66634C1.33203 7.92996 1.92898 7.33301 2.66536 7.33301H4.23212C4.49557 7.33301 4.73432 7.17785 4.84132 6.9371L7.1874 1.65842C7.27535 1.46054 7.47159 1.33301 7.68814 1.33301C8.59604 1.33301 9.33203 2.069 9.33203 2.9769V5.33301C9.33203 5.7012 9.63051 5.99967 9.9987 5.99967H12.3341C13.5599 5.99967 14.4973 7.09227 14.3109 8.30379L13.5929 12.9705Z"
                        fill={feedback === "like" ? "#9A7A4C" : "none"}
                        stroke="#9A7A4C"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={handleDislikeClick}
                    className="flex p-2 justify-center items-center gap-2 bg-primary-100 rounded-[32px]"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4"
                    >
                      <path
                        d="M11.3346 1.33366V8.66699M14.668 7.33366V2.66699C14.668 1.93061 14.071 1.33366 13.3346 1.33366H4.38381C3.39666 1.33366 2.55717 2.05388 2.40706 3.02954L1.68911 7.69621C1.50273 8.90773 2.44009 10.0003 3.66586 10.0003H6.0013C6.36949 10.0003 6.66797 10.2988 6.66797 10.667V13.0231C6.66797 13.931 7.40396 14.667 8.31186 14.667C8.52841 14.667 8.72465 14.5395 8.8126 14.3416L11.1587 9.0629C11.2657 8.82215 11.5044 8.66699 11.7679 8.66699H13.3346C14.071 8.66699 14.668 8.07004 14.668 7.33366Z"
                        fill={feedback === "dislike" ? "#9A7A4C" : "none"}
                        stroke="#9A7A4C"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={handleBookmarkClick}
                    className="flex p-2 justify-center items-center gap-2 bg-primary-100 rounded-[32px]"
                  >
                    <div className="flex w-4 h-4 justify-center items-center">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4 flex-shrink-0"
                      >
                        <path
                          d="M12 14L8 11.5L4 14V3C4 2.86739 4.05268 2.74021 4.14645 2.64645C4.24021 2.55268 4.36739 2.5 4.5 2.5H11.5C11.6326 2.5 11.7598 2.55268 11.8536 2.64645C11.9473 2.74021 12 2.86739 12 3V14Z"
                          fill={isBookmarked ? "#80653F" : "none"}
                          stroke="#80653F"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {/* Contact Text */}
            <div className="flex w-[260px] justify-center items-center px-4 py-[7px] bg-primary-200 rounded-[32px] relative -mt-[33px]">
              <div className="flex items-center gap-2">
                <span
                  className="text-sm font-medium text-grayscale-1000 text-center font-grotesk"
                  style={{
                    fontFeatureSettings: "'liga' off, 'clig' off",
                  }}
                >
                  Select Tech to Contact
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Version */}
      <div className="sm:hidden w-full bg-alternate-bg shadow-xl flex flex-col font-grotesk max-h-[90vh] rounded-2xl relative utech-nocopy">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 flex p-2 justify-center items-center gap-2 rounded-full bg-grayscale-1200 shadow-custom-light"
        >
          <CloseIcon />
        </button>

        <div className="p-4 flex flex-col gap-4 overflow-y-auto hide-scrollbar flex-1">
          {/* Relevance Score Badge */}
          {hasBadge && (
          <div className="flex items-center gap-[2.79px] self-stretch">
            <div className="flex items-center justify-center gap-[10px] px-2 py-1 bg-primary-100 rounded-[56px]">
              <span className="text-[11px] font-normal text-primary-og-600 font-grotesk">
                {typeof relevanceScore === 'number' ? `${relevanceScore}% relevance score` : relevanceScore}
              </span>
            </div>
          </div>
          )}

          {/* Title and Location */}
          <div className="flex flex-col items-start gap-2 self-stretch">
            <h2 className="text-base font-medium text-grayscale-1300 leading-normal self-stretch font-grotesk">
              {title}
            </h2>
            <div className="flex items-center gap-[11.159px] self-stretch">
              <div className="flex items-center gap-[0.93px]">
                <div className="flex w-4 h-4 flex-col justify-center items-center aspect-square">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0">
                    <path d="M2.99991 6.49994C2.99991 10.9999 7.99991 14.4999 7.99991 14.4999C7.99991 14.4999 12.9999 10.9999 12.9999 6.49994C12.9999 5.17386 12.4731 3.90209 11.5354 2.96441C10.5978 2.02672 9.32599 1.49994 7.99991 1.49994C6.67383 1.49994 5.40206 2.02672 4.46437 2.9644C3.52669 3.90209 2.99991 5.17386 2.99991 6.49994Z" fill="#9A7A4C" />
                    <path d="M8.00003 8.50003C6.89546 8.50003 6.00003 7.6046 6.00003 6.50003C6.00003 5.39546 6.89546 4.50003 8.00003 4.50003C9.1046 4.50003 10 5.39546 10 6.50003C10 7.6046 9.1046 8.50003 8.00003 8.50003Z" stroke="white" strokeWidth="0.929883" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span className="text-[11px] font-normal text-grayscale-1000 font-grotesk">
                  {institution}, {location}
                </span>
              </div>
            </div>
          </div>
          
          <div className="w-full h-[0.5px] bg-primary-300"></div>

          {/* Tech Titles Section with scroll handling */}
            <div className="flex flex-col items-start gap-2 self-stretch">
                <h3 className="text-grayscale-1200 font-grotesk font-semibold text-sm flex-shrink-0">Tech Cards ({techs.length})</h3>
                <div className="flex flex-col items-start gap-2 self-stretch max-h-[300px] overflow-y-auto">
                    {displayedTechs.map((tech) => (
                        <button
                            key={tech.id}
                            onClick={() => onTechCardClick(tech.id)}
                            className="w-full text-left p-2 px-3 items-start self-stretch rounded-lg bg-secondary-300/10 hover:bg-secondary-300/20 transition-colors flex-shrink-0"
                        >
                        <span
                            className="text-secondary-500 font-grotesk text-xs font-medium leading-normal underline"
                        >
                            {tech.title}
                        </span>
                        </button>
                    ))}
                    {techs.length > 5 && !showAllTechs && (
                        <div className="w-full flex justify-center flex-shrink-0">
                            <button
                            onClick={() => setShowAllTechs(true)}
                            className="text-primary-og-600 font-grotesk text-sm font-bold mt-1"
                            >
                            Show more
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
        
        {/* Footer */}
        <div className="p-4 mt-auto border-t border-gray-200">
            <div className="flex justify-between items-center w-full gap-3">
                <div className="flex-1 flex justify-center items-center px-3 py-2 bg-primary-100 rounded-full">
                    <span className="text-xs text-grayscale-1000 text-center font-grotesk">
                    Select Tech to Contact
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={handleLikeClick} className="flex p-2 justify-center items-center gap-2 bg-primary-100 rounded-full">
                         <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-4 h-4">
                            <path d="M13.5929 12.9705C13.4428 13.9461 12.6033 14.6663 11.6162 14.6663H2.66536C1.92898 14.6663 1.33203 14.0694 1.33203 13.333V8.66634C1.33203 7.92996 1.92898 7.33301 2.66536 7.33301H4.23212C4.49557 7.33301 4.73432 7.17785 4.84132 6.9371L7.1874 1.65842C7.27535 1.46054 7.47159 1.33301 7.68814 1.33301C8.59604 1.33301 9.33203 2.069 9.33203 2.9769V5.33301C9.33203 5.7012 9.63051 5.99967 9.9987 5.99967H12.3341C13.5599 5.99967 14.4973 7.09227 14.3109 8.30379L13.5929 12.9705Z" fill={feedback === 'like' ? '#9A7A4C' : 'none'} stroke="#9A7A4C"/>
                        </svg>
                    </button>
                    <button onClick={handleDislikeClick} className="flex p-2 justify-center items-center gap-2 bg-primary-100 rounded-full">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-4 h-4">
                            <path d="M11.3346 1.33366V8.66699M14.668 7.33366V2.66699C14.668 1.93061 14.071 1.33366 13.3346 1.33366H4.38381C3.39666 1.33366 2.55717 2.05388 2.40706 3.02954L1.68911 7.69621C1.50273 8.90773 2.44009 10.0003 3.66586 10.0003H6.0013C6.36949 10.0003 6.66797 10.2988 6.66797 10.667V13.0231C6.66797 13.931 7.40396 14.667 8.31186 14.667C8.52841 14.667 8.72465 14.5395 8.8126 14.3416L11.1587 9.0629C11.2657 8.82215 11.5044 8.66699 11.7679 8.66699H13.3346C14.071 8.66699 14.668 8.07004 14.668 7.33366Z" fill={feedback === 'dislike' ? '#9A7A4C' : 'none'} stroke="#9A7A4C" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                    <button onClick={handleBookmarkClick} className="flex p-2 justify-center items-center gap-2 bg-primary-100 rounded-full">
                        <div className="flex w-4 h-4 justify-center items-center">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0">
                                <path d="M12 14L8 11.5L4 14V3C4 2.86739 4.05268 2.74021 4.14645 2.64645C4.24021 2.55268 4.36739 2.5 4.5 2.5H11.5C11.6326 2.5 11.7598 2.55268 11.8536 2.64645C11.9473 2.74021 12 2.86739 12 3V14Z" fill={isBookmarked ? '#80653F' : 'none'} stroke="#80653F" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                    </button>
                </div>
            </div>
        </div>

      </div>
    </>
  );
}